import { lazy, Suspense } from "react"
import { Outlet, RouterProvider, createBrowserRouter, Navigate } from "react-router-dom"
import { AppHeader } from "@/features/auth/AppHeader"
import { Sidebar } from "@/features/layout/Sidebar"
import {
  RequireAuth,
  RedirectIfAuthed,
  MustChangePasswordGuard,
} from "@/router/guards"

const LoginPage = lazy(() => import("@/features/auth/LoginPage"))
const DashboardHome = lazy(() => import("@/features/dashboard/DashboardHome"))
const UploadCenterPage = lazy(() => import("@/features/uploads/UploadCenterPage"))
const KnowledgeLibraryPage = lazy(() => import("@/features/knowledge/KnowledgeLibraryPage"))
const VipHandoffPage = lazy(() => import("@/features/vip-handoff/VipHandoffPage"))
const ReviewQueuePage = lazy(() => import("@/features/review/ReviewQueuePage"))
const ReviewDetailPage = lazy(() => import("@/features/review/ReviewDetailPage"))
const StaffManagementPage = lazy(() => import("@/features/staff/StaffManagementPage"))
const AuditLogPage = lazy(() => import("@/features/audit/AuditLogPage"))
const EvolutionConsolePage = lazy(() => import("@/features/evolution/EvolutionConsolePage"))
const TravelAgenciesPage = lazy(() => import("@/features/travel-agencies/TravelAgenciesPage"))
const ProfilePage = lazy(() => import("@/features/profile/ProfilePage"))
const CounselorCtaSettingsPage = lazy(() => import("@/features/settings/CounselorCtaSettingsPage"))
// B3.1 — API Key 管理
const ApiKeysListPage = lazy(() => import("@/features/api_keys/ApiKeysListPage"))

function PageFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="animate-pulse text-sm text-muted-foreground">加载中...</div>
    </div>
  )
}

function AppShell() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader />
      <div className="flex flex-1">
        <Sidebar />
        <main className="flex-1 overflow-y-auto">
          <MustChangePasswordGuard>
            <Suspense fallback={<PageFallback />}>
              <Outlet />
            </Suspense>
          </MustChangePasswordGuard>
        </main>
      </div>
    </div>
  )
}

const router = createBrowserRouter([
  {
    path: "/login",
    element: (
      <RedirectIfAuthed>
        <Suspense fallback={<PageFallback />}>
          <LoginPage />
        </Suspense>
      </RedirectIfAuthed>
    ),
  },
  {
    path: "/",
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: "dashboard", element: <DashboardHome /> },
      { path: "profile", element: <ProfilePage /> },
      {
        // 注意：用 /upload-center 避免与后端 StaticFiles /uploads/* 冲突（OSS local URL）
        path: "upload-center",
        element: <UploadCenterPage />,
      },
      {
        path: "knowledge-library",
        element: (
          <RequireAuth minRole="operator">
            <KnowledgeLibraryPage />
          </RequireAuth>
        ),
      },
      {
        path: "vip-handoff",
        element: (
          <RequireAuth minRole="advisor">
            <VipHandoffPage />
          </RequireAuth>
        ),
      },
      {
        path: "settings/vip-counselor",
        element: (
          <RequireAuth minRole="admin">
            <CounselorCtaSettingsPage />
          </RequireAuth>
        ),
      },
      {
        path: "review",
        element: (
          <RequireAuth minRole="operator">
            <ReviewQueuePage />
          </RequireAuth>
        ),
      },
      {
        path: "review/:jobId",
        element: (
          <RequireAuth minRole="operator">
            <ReviewDetailPage />
          </RequireAuth>
        ),
      },
      {
        path: "staff",
        element: (
          <RequireAuth minRole="admin">
            <StaffManagementPage />
          </RequireAuth>
        ),
      },
      {
        path: "audit-logs",
        element: (
          <RequireAuth minRole="admin">
            <AuditLogPage />
          </RequireAuth>
        ),
      },
      {
        path: "evolution",
        element: (
          <RequireAuth minRole="admin">
            <EvolutionConsolePage />
          </RequireAuth>
        ),
      },
      {
        path: "travel-agencies",
        element: (
          <RequireAuth minRole="operator">
            <TravelAgenciesPage />
          </RequireAuth>
        ),
      },
      {
        path: "api-keys",
        element: (
          <RequireAuth minRole="admin">
            <ApiKeysListPage />
          </RequireAuth>
        ),
      },
    ],
  },
  {
    path: "*",
    element: <Navigate to="/dashboard" replace />,
  },
])

export function AppRouter() {
  return <RouterProvider router={router} />
}
